// client/src/user/dashboard/chat/MessageBubble.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  useTheme,
  alpha,
  Collapse,
  Button,
} from "@mui/material";
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import MarkdownRenderer from "./MarkdownRenderer";
import CopyButton from "./CopyButton";

const COLLAPSE_THRESHOLD = 500;

const MessageBubble = ({ message, lang, isStreaming }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isUser = message.role === "user";
  const isLong = isUser && message.content?.length > COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isUser ? "flex-end" : "flex-start",
        mb: { xs: 2.25, sm: 3 },
        width: "100%",
        px: 0,
        boxSizing: "border-box",
        minWidth: 0,
        animation: "fadeInUp 0.2s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 1.5 },
          width: "100%",
          maxWidth: "100%",
          flexDirection: isUser ? "row-reverse" : "row",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Avatar
          sx={{
            width: { xs: 30, sm: 32 },
            height: { xs: 30, sm: 32 },
            flexShrink: 0,
            mt: 0.25,
            background: isUser
              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
              : alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
            border: isUser
              ? "none"
              : `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: isUser
              ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`
              : "none",
          }}
        >
          {isUser ? (
            <PersonIcon
              sx={{
                fontSize: 16,
                color: theme.palette.primary.contrastText,
              }}
            />
          ) : (
            <BotIcon sx={{ fontSize: 16, color: "primary.main" }} />
          )}
        </Avatar>

        <Box
          sx={{
            minWidth: 0,
            maxWidth: {
              xs: isUser ? "86%" : "calc(100% - 40px)",
              sm: isUser ? "78%" : "calc(100% - 48px)",
            },
            flex: isUser ? "0 1 auto" : "1 1 auto",
            overflow: "hidden",
            bgcolor: isUser
              ? alpha(theme.palette.primary.main, isDark ? 0.15 : 0.08)
              : "transparent",
            borderRadius: isUser ? "18px 4px 18px 18px" : 0,
            px: isUser ? { xs: 1.5, sm: 2 } : 0,
            py: isUser ? { xs: 1.15, sm: 1.5 } : 0,
            border: isUser
              ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              : "none",
            boxShadow: isUser
              ? `0 1px 4px ${alpha(theme.palette.primary.main, 0.1)}`
              : "none",
          }}
        >
          {isUser ? (
            <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
              <Collapse
                in={!isLong || expanded}
                collapsedSize={isLong ? 120 : undefined}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.primary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    fontSize: { xs: "0.9rem", sm: "0.9375rem" },
                    lineHeight: 1.7,
                  }}
                >
                  {message.content}
                </Typography>
              </Collapse>

              {isLong && !expanded && (
                <Box
                  sx={{
                    mt: -3,
                    height: 40,
                    background: `linear-gradient(to bottom, transparent, ${alpha(
                      theme.palette.primary.main,
                      isDark ? 0.15 : 0.08,
                    )})`,
                    borderRadius: "0 0 4px 18px",
                    pointerEvents: "none",
                  }}
                />
              )}

              {isLong && (
                <Button
                  size="small"
                  onClick={() => setExpanded((p) => !p)}
                  startIcon={
                    expanded ? (
                      <ExpandLessIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: 14 }} />
                    )
                  }
                  sx={{
                    mt: 0.5,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: "primary.main",
                    textTransform: "none",
                    p: 0,
                    minWidth: 0,
                    "&:hover": { bgcolor: "transparent", opacity: 0.8 },
                  }}
                >
                  {expanded
                    ? lang?.showLess || "Show less"
                    : lang?.showMore ||
                      `Show more (${message.content.length} chars)`}
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {isStreaming && !message.content ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.6,
                    py: 1.5,
                    px: 0.5,
                    alignItems: "center",
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        animation: "bounce 1.4s ease-in-out infinite",
                        animationDelay: `${i * 0.18}s`,
                        "@keyframes bounce": {
                          "0%, 80%, 100%": {
                            opacity: 0.3,
                            transform: "scale(0.75)",
                          },
                          "40%": { opacity: 1, transform: "scale(1.15)" },
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <MarkdownRenderer content={message.content} />
              )}

              {!isStreaming && message.content && (
                <Box
                  sx={{
                    mt: 0.75,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <CopyButton text={message.content} lang={lang} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageBubble;
